import { createContext, useContext } from 'react';

import { Tab, TabGroupProps } from '@headlessui/react';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { transitionStandard } from '@utils/transitions';

type PillTabSize = 'md' | 'sm';

const PillTabSizeContext = createContext<PillTabSize>('md');

export type PillTabsProps = TabGroupProps<any> & {
  size?: PillTabSize;
};

export function PillTabs({ size = 'md', children, ...rest }: PillTabsProps) {
  return (
    <PillTabSizeContext.Provider value={size}>
      <Tab.Group {...rest}>{children}</Tab.Group>
    </PillTabSizeContext.Provider>
  );
}

function PillTab({ label, count }: { label: string; count: number }) {
  const size = useContext(PillTabSizeContext);
  return (
    <StyledTab $isSmall={size === 'sm'}>
      <span>{label}</span>
      <Count>{count}</Count>
    </StyledTab>
  );
}

const StyledTab = styled(Tab)<{ $isSmall?: boolean }>(({ $isSmall }) => ({
  transition: transitionStandard('all'),
  height: $isSmall ? '2rem' : theme.base.height,
  display: 'flex',
  alignItems: 'center',
  padding: $isSmall ? '0 1rem' : '0 1.5rem',
  borderRadius: '1.25rem',
  backgroundColor: theme.color.uiTrans5,
  color: theme.color.textSecondary,
  border: 'none',
  outline: 'none',
  font: theme.font.semibold14,
  whiteSpace: 'nowrap',

  '&[aria-selected="true"]': {
    backgroundColor: theme.color.elementPurple100,
    color: theme.color.white100,
  },
  '&[aria-selected="false"]': {
    '&:hover': {
      backgroundColor: theme.color.uiTrans10,
      cursor: 'pointer',
    },
    '&:active': {
      transform: 'scale(0.97)',
    },
  },
  '&:focus-visible': {
    '--shadow-focus-color': theme.color.border100,
    boxShadow: theme.shadow.focus,
  },
}));

const Count = styled.span({
  color: theme.color.textHelper,
  display: 'inline-block',
  marginLeft: '0.5rem',
  [`${StyledTab}[aria-selected="true"] &`]: {
    color: theme.color.white100,
  },
});

const StyledTabList = styled(Tab.List)({
  display: 'flex',
  alignItems: 'center',
  'button:not(:first-child)': {
    marginLeft: '0.5rem',
  },
});

PillTabs.Tab = PillTab;
PillTabs.Tabs = StyledTabList;
PillTabs.Panels = Tab.Panels;
PillTabs.Panel = Tab.Panel;

PillTabs.Panels.displayName = 'PillTabs.Panels';
PillTabs.Panel.displayName = 'PillTabs.Panel';
